import "../../../resources/less/Admin/Login/FamilyEmailGate.less";
import OnboardingApi from "../../Family/Onboarding/OnboardingApi";
import NotMyAccount from "./NotMyAccount";
import {
  failureMessage,
  isValidEmail,
} from "../../Family/Onboarding/OnboardingRules";

/**
 * The family login popup on the family host. One email decides: unknown, the visitor is
 * welcomed and sent to the onboarding page; known, the same card asks for the password and
 * posts the login form; known on the school side, the card asks which version the visitor wants
 * first (the school one opens the school login on that email, the family one asks the password
 * here and the login then starts the family onboarding). The two ways around it: create an
 * account without an email, or the school version's login. Under the password, « Ce n'est pas
 * votre compte? » (NotMyAccount) mails the address for whoever owns the mailbox.
 */
export default class FamilyEmailGate {
  private readonly panels: JQuery<HTMLElement>;
  private readonly emailInput: JQuery<HTMLElement>;
  private readonly passwordInput: JQuery<HTMLElement>;
  private readonly usernameInput: JQuery<HTMLElement>;
  private readonly passwordTitle: JQuery<HTMLElement>;
  private readonly error: JQuery<HTMLElement>;
  private readonly loginError: JQuery<HTMLElement>;
  private readonly schoolError: JQuery<HTMLElement>;
  /** « Ce n'est pas votre compte? » under the password */
  private readonly notMine: NotMyAccount;
  private readonly signupUrl: string;
  /** The email a login just failed with: the first opening goes straight to its password */
  private failedEmail: string | null;
  /** That login was on the way to the family version: the password panel keeps those words */
  private failedForFamily: boolean;
  /** A version was chosen and sent on this card: leaving the card takes it back */
  private chosen = false;
  /** Counts the resets: a choice answered after one is a choice nobody waits for */
  private generation = 0;
  private busy = false;

  constructor(
    private readonly root: JQuery<HTMLElement>,
    private readonly api: OnboardingApi,
    private readonly onSchool: () => void,
    /** A school account wants the school version: the school login opens on its email */
    private readonly onSchoolAccount: (email: string) => void,
    private readonly navigate: (url: string) => void,
  ) {
    this.panels = root.find(".js-gate-panel");
    this.emailInput = root.find(".js-gate-email");
    this.passwordInput = root.find(".js-gate-password");
    this.usernameInput = root.find(".js-gate-username");
    this.passwordTitle = root.find(".js-gate-password-title");
    this.error = root.find(".js-gate-error");
    this.loginError = root.find(".js-gate-login-error");
    this.schoolError = root.find(".js-gate-school-error");
    this.signupUrl = root.attr("data-signup-url") ?? "";
    this.failedEmail = root.attr("data-failed-email") ?? null;
    this.failedForFamily = root.attr("data-failed-intent") === "famille";
    this.notMine = new NotMyAccount(root.find(".js-gate-not-mine"), api);
    this.bind();
  }

  /**
   * @param onSchool The visitor wants the school version: the popup switches to its login
   * @param onSchoolAccount A school account picked the school version: its login, on that email
   * @param initialEmail An email the page arrived with (the signup page sends a known one here)
   */
  public static Build(
    root: JQuery<HTMLElement>,
    onSchool: () => void,
    onSchoolAccount: (email: string) => void,
    initialEmail = "",
  ): FamilyEmailGate | null {
    if (root.length === 0) {
      return null;
    }
    const gate = new FamilyEmailGate(
      root,
      new OnboardingApi(
        root.attr("data-email-url") ?? "",
        root.attr("data-version-choice-url") ?? "",
        "",
        "",
        "",
        root.attr("data-not-mine-url") ?? "",
      ),
      onSchool,
      onSchoolAccount,
      (url) => {
        window.location.href = url;
      },
    );
    if (initialEmail !== "") {
      gate.emailInput.val(initialEmail);
    }
    return gate;
  }

  /** The page reloaded on a login the gate's own form failed. */
  public HasFailedLogin(): boolean {
    return this.failedEmail !== null;
  }

  public Reset(): void {
    this.generation += 1;
    this.showPanel("ask");
    this.error.addClass("hide");
    this.schoolError.addClass("hide");
    this.passwordInput.val("");
    this.forgetChoice();
  }

  /** The server forgets a choice that was backed out of; it would expire on its own anyway. */
  private forgetChoice(): void {
    if (!this.chosen) {
      return;
    }
    this.chosen = false;
    this.api.ForgetVersion().catch(() => undefined);
  }

  /**
   * The popup just opened on the gate: the caret goes to the email, as the prototype does. After
   * a failed login the password panel comes back instead, its error line still showing.
   */
  public Open(): void {
    if (this.failedEmail !== null && this.failedEmail !== "") {
      // The choice behind that attempt still stands on the server: leaving the card takes it back
      this.chosen = this.failedForFamily;
      this.showPassword(this.failedEmail, true, this.failedForFamily);
      this.failedEmail = null;
      return;
    }
    this.emailInput.trigger("focus");
  }

  private bind(): void {
    this.root.on("click", ".js-gate-continue", () => {
      void this.check();
    });
    this.root.on("keydown", ".js-gate-email", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        void this.check();
      }
    });
    // The onboarding page is on the family host: the email travels with the link, whatever host asked it
    this.root.on("click", ".js-gate-go, .js-gate-create", () => {
      const email = String(this.emailInput.val() ?? "").trim();
      this.navigate(
        email === ""
          ? this.signupUrl
          : `${this.signupUrl}?email=${encodeURIComponent(email)}`,
      );
    });
    this.root.on("click", ".js-gate-school", () => {
      this.forgetChoice();
      this.onSchool();
    });
    this.root.on("click", ".js-gate-choose", (event) => {
      const card: JQuery<HTMLElement> = $(event.currentTarget as HTMLElement);
      const choice = card.attr("data-choice");
      void this.choose(choice === "famille" ? "famille" : "scolaire", card);
    });
    this.root.on("click", ".js-gate-change-email", () => {
      this.Reset();
      this.emailInput.trigger("focus");
    });
  }

  private async check(): Promise<void> {
    const email = String(this.emailInput.val() ?? "").trim();
    if (!isValidEmail(email)) {
      this.error
        .text(this.error.attr("data-invalid") ?? "")
        .removeClass("hide");
      this.emailInput.trigger("focus");
      return;
    }
    if (this.busy) {
      return;
    }
    this.busy = true;
    this.error.addClass("hide");
    this.writeEmailLine(".js-gate-checking", email);
    this.showPanel("checking");
    try {
      const answer = await this.api.CheckEmail(email);
      if (answer.status === "exists") {
        this.showPassword(email, false, false);
      } else if (answer.status === "school") {
        this.showSchool(email);
      } else {
        this.showPanel("welcome");
      }
    } catch (failure) {
      // The server's words when it had some (a refused email), the page's otherwise
      this.error
        .text(failureMessage(failure) ?? this.error.attr("data-failed") ?? "")
        .removeClass("hide");
      this.showPanel("ask");
    } finally {
      this.busy = false;
    }
  }

  /**
   * The school account said which version it wants. The server keeps the word for the login that
   * follows: the school one happens on the school form, the family one right here. A popup closed
   * while the word travels takes it back once it has arrived.
   */
  private async choose(
    choice: "scolaire" | "famille",
    card: JQuery<HTMLElement>,
  ): Promise<void> {
    if (this.busy) {
      return;
    }
    const email = String(this.emailInput.val() ?? "").trim();
    const generation = this.generation;
    this.busy = true;
    this.schoolError.addClass("hide");
    // Nothing else moves while the choice travels: no second choice, no other email
    const controls = this.root.find(".js-gate-choose, .js-gate-change-email");
    controls.prop("disabled", true);
    try {
      await this.api.ChooseVersion(email, choice);
      this.chosen = true;
    } catch (failure) {
      this.schoolError
        .text(
          failureMessage(failure) ?? this.schoolError.attr("data-failed") ?? "",
        )
        .removeClass("hide");
      controls.prop("disabled", false);
      card.trigger("focus");
      return;
    } finally {
      this.busy = false;
      controls.prop("disabled", false);
    }
    if (generation !== this.generation) {
      this.forgetChoice();
      return;
    }
    if (choice === "scolaire") {
      this.onSchoolAccount(email);
    } else {
      this.showPassword(email, false, true);
    }
  }

  /** « On se connaît déjà! »: the email in bold, the two versions to pick from. */
  private showSchool(email: string): void {
    this.writeEmailLine(".js-gate-school", email);
    this.showPanel("school");
    this.root.find(".js-gate-choose").first().trigger("focus");
  }

  /**
   * The password panel for a known email: the form posts that email as the username. On the way
   * to the family version the title says so.
   */
  private showPassword(
    email: string,
    withError: boolean,
    forFamily: boolean,
  ): void {
    const text = this.root.find(".js-gate-password-text");
    this.writeEmailLine(
      ".js-gate-password",
      email,
      forFamily ? text.attr("data-text-family") : text.attr("data-text"),
    );
    this.passwordTitle.text(
      this.passwordTitle.attr(forFamily ? "data-family" : "data-default") ?? "",
    );
    this.usernameInput.val(email);
    this.emailInput.val(email);
    this.notMine.Show(email);
    this.loginError.toggleClass("hide", !withError);
    this.showPanel("password");
    this.passwordInput.trigger("focus");
  }

  /**
   * « On regarde si <email> est déjà chez nous. », the email in bold and as text, never as
   * markup. The sentence is the line's own unless one is given.
   */
  private writeEmailLine(prefix: string, email: string, text?: string): void {
    const line = this.root.find(`${prefix}-text`);
    const [before, after] = (text ?? line.attr("data-text") ?? "").split(
      "%email%",
    );
    line.find(`${prefix}-before`).text(before ?? "");
    line.find(`${prefix}-email`).text(email);
    line.find(`${prefix}-after`).text(after ?? "");
  }

  private showPanel(name: string): void {
    this.panels.addClass("hide");
    this.panels.filter(`[data-panel="${name}"]`).removeClass("hide");
  }
}
